import { GeoZoneCoordinates, GeoZoneType, Location } from '@EcamModel/model';
import { Zones } from '@EcamModel/model/Zones';
import IcMenu from '@assets/details-camera-icons/IcMenu';
import IcDocument from '@assets/jsx-icon/IcDocument';
import IcDocumentFilter from '@assets/jsx-icon/IcDocumentFilter';
import BaseCheckbox from '@components/BaseCheckbox';
import IOSSwitch from '@components/IOSSwitch';
import PopUpWarning from '@components/PopUpWarning';
import BreadCrumbs from '@components/breadcumbs/BreadCumbs';
import StyledAutocomplete from '@components/styled-autocomplete';
import {
    Box,
    Button,
    CircularProgress,
    Fade,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { DetectMode, detectModes, OperationHourHistory, OperationHourSection } from '@pages/cameras/details/components/EditGeoZones';
import NoDataCameras from '@pages/cameras/list/components/NoDataCameras';
import { Controller, FormProvider } from 'react-hook-form';
import { BsQuestion } from 'react-icons/bs';
import { HeadingSection } from '../../../../AddGeoZones';
import PopUpEditablePeriods from '../../../../PopUpEditablePeriods';
import useEditGeoZones from '../hook/useEditGeoZones';
import FramePolygonGeoOverview from '../../../components/FramePolygonGeoOverview';
import { Mode } from '../../../../CameraInformation';
import { LoadingButton } from '@mui/lab';
import LoadingBreadCrumbs from '@components/skeleton/LoadingBreadCrumbs';
import { BaseTextField } from '@components/BaseTextField';
import color from '@theme/Colors';
import { formatDateByTimeZone, formatUKDate } from 'src/helpers';

export default function EditGeoZonesPTZ() {
    const {
        navigate,
        isValid,
        isDirty,
        methods,
        loading,
        isLoading,
        isLoadingSave,
        isEdit,
        conditionBreadcrumbs,
        selectedTab,
        setSelectedTab,
        mode,
        setMode,
        selectedScheduleOperationHours,
        setSelectedScheduleOperationHours,

        contraventionAutocomplete,
        detectModeAutocomplete,

        _nextOperationHours,
        _currentOperationHours,
        currentOperationHours,
        currentGeoZoneConfigs,
        currentSelectedPeriodConfigs,
        filterPreGeoZones,
        geoZonesItem,
        imageElement,
        scheduleOperationConfigs,
        popUpEditablePeriods,
        popUpDeletePeriods,
        nextGeoZoneConfigs,
        nextOperationHours,
        nextPeriodConfigs,

        handleConfirm,
        handleDelete,
        handleEditGeoZone,
        handleNavigateButton,

        loadingListGzByView,
        locationAutocomplete,
        zoneAutocomplete,
        zones,
        setZones,
    } = useEditGeoZones({});

    const setValue = methods.setValue as (name: string, value: any, options?: Record<string, boolean>) => void;

    const handleDeleteGeoZonePoints = () => {
        setValue('geoZonePoints', [] as GeoZoneCoordinates[], {
            shouldValidate: true,
            shouldTouch: true,
            shouldDirty: true,
        });
    };

    return (
        <Stack mt={1}>
            <Stack>
                <Typography variant="h3">{'Edit geo-zones'}</Typography>
                {loading ? <LoadingBreadCrumbs /> : <BreadCrumbs breadcrumbs={conditionBreadcrumbs} />}
            </Stack>
            <FormProvider {...methods}>
                {geoZonesItem?.GeoZoneType === GeoZoneType.Overview && (
                    <>
                        {/* Draw angle box */}
                        <HeadingSection icon={<IcMenu />} heading="Zone marking" />

                        {/* Add GeoZone Name field for Overview type */}
                        <Controller
                            name="geoZoneName"
                            control={methods.control}
                            render={({ field, fieldState: { error } }) => (
                                <BaseTextField
                                    {...field}
                                    label="Name"
                                    isVariantOutlined
                                    disabled={loading}
                                    placeholder={'Geozone name'}
                                    maxLength={50}
                                    error={!!error?.message}
                                    helperText={error?.message}
                                    sx={{ width: '100%', mt: 2 }}
                                />
                            )}
                        />

                        <Controller
                            name="geoZonePoints"
                            control={methods.control}
                            render={({ field, fieldState: { error } }) => (
                                <FramePolygonGeoOverview
                                    {...field}
                                    imageElement={loadingListGzByView ? undefined : imageElement!}
                                    mode={Mode.edit}
                                    prevGeoZone={filterPreGeoZones as GeoZoneCoordinates[][]}
                                    selectedGeoZone={geoZonesItem}
                                    onChange={(points) => {
                                        setValue('geoZonePoints', points, {
                                            shouldValidate: true,
                                            shouldTouch: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                    onDelete={handleDeleteGeoZonePoints}
                                />
                            )}
                        />
                    </>
                )}

                {geoZonesItem?.GeoZoneType === GeoZoneType.Detail && (
                    <Box mt={3}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Controller
                                name="contraventionReasonTranslations"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                    <StyledAutocomplete
                                        {...field}
                                        {...contraventionAutocomplete}
                                        isRequired
                                        getOptionLabel={(o) => o.Summary}
                                        disabled={loading}
                                        label="Contravention reason"
                                        value={field.value}
                                        placeholder="Select reason"
                                        disabledAllOption
                                        onChange={(c) => {
                                            setValue('contraventionReasonTranslations', c!, {
                                                shouldValidate: true,
                                                shouldTouch: true,
                                                shouldDirty: true,
                                            });
                                        }}
                                        wrapperWidth="100%"
                                    />
                                )}
                            />
                            <Controller
                                name="detectMode"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                    <StyledAutocomplete
                                        {...field}
                                        {...detectModeAutocomplete}
                                        isRequired
                                        getOptionLabel={(o) => o.Label}
                                        customRenderOption={(_, option) => renderDetectModesOption(option, detectModes)}
                                        disabled={loading}
                                        label="Detect mode"
                                        value={field.value}
                                        disabledAllOption
                                        onChange={(c) => {
                                            field.onChange(c);
                                        }}
                                        wrapperWidth="100%"
                                    />
                                )}
                            />
                            <div>
                                <Controller
                                    name="isActive"
                                    control={methods.control}
                                    render={({ field, fieldState: { error } }) => (
                                        <IOSSwitch
                                            {...field}
                                            label={
                                                <Typography sx={{ minWidth: '55px' }}>
                                                    {!!field.value ? 'Active' : 'Inactive'}
                                                </Typography>
                                            }
                                            iosSwitchProps={{
                                                checked: field.value,
                                                disabled: loading,
                                                onChange(event, checked) {
                                                    field.onChange(event);
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        </Stack>

                        <Stack direction="row" justifyContent="start" spacing={2} mt={2}>
                            <Controller
                                name="geoZoneName"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                    <BaseTextField
                                        {...field}
                                        label="Name"
                                        isVariantOutlined
                                        disabled={loading}
                                        placeholder={'Geozone name'}
                                        maxLength={50}
                                        error={!!error?.message}
                                        helperText={error?.message}
                                    />
                                )}
                            />

                            <Controller
                                name="totalBay"
                                control={methods.control}
                                rules={{
                                    pattern: {
                                        value: /^[0-9]+$/,
                                        message: 'Please enter a valid Number.',
                                    },
                                }}
                                render={({ field, fieldState: { error } }) => (
                                    <BaseTextField
                                        {...field}
                                        label="Total bay"
                                        isVariantOutlined
                                        disabled={loading}
                                        placeholder={'Total bay'}
                                        error={!!error?.message}
                                        helperText={error?.message}
                                        onChange={(e) => {
                                            field.onChange(
                                                isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value)
                                            );
                                        }}
                                    />
                                )}
                            />

                            <Box width={{ xs: '300px', md: '250px', lg: '220px' }}></Box>
                        </Stack>

                        {/* checkbox ExemptVRNs */}
                        <Stack direction="row" alignItems="center" justifyContent={'space-between'} width="100%" mt={2}>
                            <Stack direction="row" alignItems="center">
                                <Controller
                                    name="isExemptVRNs"
                                    control={methods.control}
                                    render={({ field, fieldState: { error } }) => (
                                        <FormControlLabel
                                            control={
                                                <BaseCheckbox
                                                    checked={field.value}
                                                    onChange={(_, checked) => {
                                                        field.onChange(checked);
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    disabled={loading}
                                                />
                                            }
                                            label={
                                                <Typography ml={0.5} mt={0.3}>
                                                    Exempt VRNs on Permit lists
                                                </Typography>
                                            }
                                            sx={{ ml: 0 }}
                                        />
                                    )}
                                />
                            </Stack>
                        </Stack>
                        {/* end checkbox ExemptVRNs  */}

                        {/* Location and Zone */}
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mt={2}>
                            <Controller
                                name="location"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                    <StyledAutocomplete
                                        {...field}
                                        {...locationAutocomplete}
                                        getOptionLabel={(o) => (o as Location)?.Name}
                                        label="Location"
                                        value={field.value}
                                        placeholder="Select location"
                                        disabledAllOption
                                        disabled={loading}
                                        onChange={(l: Location) => {
                                            setValue('location', l!, {
                                                shouldValidate: true,
                                                shouldTouch: true,
                                                shouldDirty: true,
                                            });
                                            setValue('zone', undefined as any);
                                            setZones(l?.Zones || []);
                                            if (l?.Zones && l.Zones.length > 0) {
                                                setValue('zone', l.Zones[0]);
                                            }
                                        }}
                                        wrapperWidth="100%"
                                    />
                                )}
                            />
                            <Controller
                                name="zone"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                    <StyledAutocomplete
                                        {...field}
                                        {...zoneAutocomplete}
                                        getOptionLabel={(o) => `${(o as Zones)?.PublicName} (ZoneID ${(o as Zones)?.Id})`}
                                        label="Zone"
                                        value={field.value}
                                        placeholder="Select zone"
                                        disabledAllOption
                                        disabled={!methods.watch().location || loading}
                                        onChange={(z: Zones) => {
                                            setValue('zone', z!, {
                                                shouldValidate: true,
                                                shouldTouch: true,
                                                shouldDirty: true,
                                            });
                                        }}
                                        wrapperWidth="100%"
                                    />
                                )}
                            />
                        </Stack>
                        {/* end Location and Zone */}

                        {/* Draw angle box */}
                        <HeadingSection icon={<IcMenu />} heading="Zone marking" />
                        <Controller
                            name="geoZonePoints"
                            control={methods.control}
                            render={({ field, fieldState: { error } }) => (
                                <FramePolygonGeoOverview
                                    {...field}
                                    imageElement={loadingListGzByView ? undefined : imageElement!}
                                    mode={Mode.edit}
                                    prevGeoZone={filterPreGeoZones as GeoZoneCoordinates[][]}
                                    selectedGeoZone={geoZonesItem}
                                    onChange={(points) => {
                                        setValue('geoZonePoints', points, {
                                            shouldValidate: true,
                                            shouldTouch: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                    onDelete={handleDeleteGeoZonePoints}
                                />
                            )}
                        />

                        {/* Operational periods */}
                        <Stack mt={2}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {[
                                    { index: 1, title: 'Current Operational Period', icon: <IcDocument /> },
                                    {
                                        index: 2,
                                        title: `Past/Future Periods (${scheduleOperationConfigs?.length})`,
                                        icon: <IcDocumentFilter />,
                                    },
                                ].map((item) => {
                                    return (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            sx={{
                                                py: 1,
                                                px: 2,
                                                background: selectedTab === item.index ? '#E8F5E9' : '#FAFAFA',
                                                borderRadius: '8px 8px 0px 0px',
                                                cursor: 'pointer',
                                                transition: '.2s ease-in-out',
                                            }}
                                            onClick={() => {
                                                setSelectedTab(item.index);
                                            }}
                                            spacing={1}
                                        >
                                            {item.icon && (
                                                <Box
                                                    width={16}
                                                    height={16}
                                                    color={selectedTab === item.index ? '#009D4F' : '#85858A'}
                                                >
                                                    {item.icon}
                                                </Box>
                                            )}
                                            <Typography color={selectedTab === item.index ? '#009D4F' : '#85858A'}>
                                                {item.title}
                                            </Typography>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                            <Stack
                                sx={{
                                    p: 2,
                                    border: '1px solid #DDDDDD',
                                    borderRadius: '0px 8px 8px 8px',
                                }}
                            >
                                {selectedTab === 1 && (
                                    <Stack>
                                        {!!currentGeoZoneConfigs?.EffectiveFrom && (
                                            <Stack
                                                sx={{
                                                    borderBottom: '1px solid #DDDDDD',
                                                    pb: 1,
                                                }}
                                            >
                                                <Typography variant="subtitle2" fontWeight={500}>
                                                    Effective from:{' '}
                                                    {formatUKDate(currentGeoZoneConfigs?.EffectiveFrom)}
                                                </Typography>
                                                <OperationHourHistory
                                                    createdBy={currentGeoZoneConfigs?.CreatedBy!}
                                                    createdAt={formatDateByTimeZone(
                                                        currentGeoZoneConfigs?.Created,
                                                        undefined,
                                                        false
                                                    )}
                                                    updatedBy={currentGeoZoneConfigs?.UpdatedBy!}
                                                    updatedAt={formatDateByTimeZone(
                                                        currentGeoZoneConfigs?.Updated,
                                                        undefined,
                                                        false
                                                    )}
                                                />
                                            </Stack>
                                        )}
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            mt={!!currentGeoZoneConfigs?.EffectiveFrom ? 2 : 0}
                                        >
                                            <Typography fontWeight={500}>Operation hours</Typography>
                                            <Button
                                                variant="contained"
                                                disabled={loading}
                                                onClick={() => {
                                                    setMode('create');
                                                    popUpEditablePeriods.setTrue();
                                                }}
                                            >
                                                Create new
                                            </Button>
                                        </Stack>
                                        {!!currentOperationHours?.length ? (
                                            <Stack spacing={1} mt={2}>
                                                {Object.entries(_currentOperationHours).map(([key, value], index) => {
                                                    return (
                                                        <OperationHourSection
                                                            weekday={parseInt(key)}
                                                            operationHours={value}
                                                            gracePeriods={currentSelectedPeriodConfigs}
                                                        />
                                                    );
                                                })}
                                            </Stack>
                                        ) : (
                                            <Box mt={2}>
                                                <NoDataCameras />
                                            </Box>
                                        )}
                                    </Stack>
                                )}
                                {selectedTab === 2 && (
                                    <Stack>
                                        <Stack
                                            sx={{
                                                borderBottom: '1px solid #DDDDDD',
                                                pb: 1,
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                                                <Typography variant="subtitle2" fontWeight={500}>
                                                    Effective from:
                                                </Typography>
                                                <FormControl sx={{ width: '70%' }}>
                                                    <Select
                                                        displayEmpty
                                                        inputProps={{ 'aria-label': 'Without label' }}
                                                        value={selectedScheduleOperationHours || -1}
                                                        onChange={(e) => {
                                                            setSelectedScheduleOperationHours(e.target.value as any);
                                                        }}
                                                    >
                                                        <MenuItem value={-1}>Please select an option</MenuItem>
                                                        {scheduleOperationConfigs.map((item) => {
                                                            return (
                                                                <MenuItem value={item.Id}>
                                                                    {formatUKDate(item.EffectiveFrom)}
                                                                </MenuItem>
                                                            );
                                                        })}
                                                    </Select>
                                                </FormControl>
                                            </Stack>
                                            {selectedScheduleOperationHours !== -1 && (
                                                <Fade in={!!selectedScheduleOperationHours}>
                                                    <Box>
                                                        <OperationHourHistory
                                                            createdBy={nextGeoZoneConfigs?.CreatedBy!}
                                                            createdAt={formatDateByTimeZone(
                                                                nextGeoZoneConfigs?.Created,
                                                                undefined,
                                                                false
                                                            )}
                                                            updatedBy={nextGeoZoneConfigs?.UpdatedBy!}
                                                            updatedAt={formatDateByTimeZone(
                                                                nextGeoZoneConfigs?.Updated,
                                                                undefined,
                                                                false
                                                            )}
                                                        />
                                                    </Box>
                                                </Fade>
                                            )}
                                        </Stack>
                                        {selectedScheduleOperationHours !== -1 ? (
                                            <>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    mt={2}
                                                >
                                                    <Typography fontWeight={500}>Operation hours</Typography>
                                                    <Fade in={!!isEdit}>
                                                        <Stack direction="row" alignItems="center" spacing={2}>
                                                            <Button
                                                                variant="errorContained"
                                                                onClick={() => {
                                                                    popUpDeletePeriods.setTrue();
                                                                }}
                                                                sx={{ minWidth: 100 }}
                                                            >
                                                                Delete
                                                            </Button>
                                                            <Button
                                                                variant="contained"
                                                                onClick={() => {
                                                                    setMode('edit');
                                                                    popUpEditablePeriods.setTrue();
                                                                }}
                                                                sx={{ minWidth: 100 }}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </Stack>
                                                    </Fade>
                                                </Stack>
                                                {!!nextOperationHours?.length ? (
                                                    <Stack spacing={1} mt={2}>
                                                        {Object.entries(_nextOperationHours).map(
                                                            ([key, value], index) => {
                                                                return (
                                                                    <OperationHourSection
                                                                        weekday={parseInt(key)}
                                                                        operationHours={value}
                                                                        gracePeriods={nextPeriodConfigs}
                                                                    />
                                                                );
                                                            }
                                                        )}
                                                    </Stack>
                                                ) : (
                                                    <Box mt={2}>
                                                        <NoDataCameras />
                                                    </Box>
                                                )}
                                            </>
                                        ) : (
                                            <Box mt={2}>
                                                <NoDataCameras />
                                            </Box>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        </Stack>

                        <PopUpEditablePeriods
                            {...popUpEditablePeriods}
                            onClose={popUpEditablePeriods.onClose}
                            mode={mode!}
                            selectedGeoZone={geoZonesItem}
                            gracePeriodConfigs={nextPeriodConfigs as any}
                            effectiveFrom={nextGeoZoneConfigs?.EffectiveFrom}
                            selectedGeoZoneConfigsId={nextGeoZoneConfigs?.Id!}
                            onConfirm={handleConfirm}
                            isLoading={isLoading}
                            contraventionReasonId={
                                methods.watch()?.contraventionReasonTranslations?.ContraventionReasonId
                            }
                        />
                        <PopUpWarning
                            {...popUpDeletePeriods}
                            onConfirm={() => {
                                handleDelete(nextGeoZoneConfigs?.Id!);
                            }}
                            message="Are you sure you want to delete this operation period?"
                            isLoading={isLoading}
                        />
                    </Box>
                )}
            </FormProvider>
            <Stack mt={3} direction={'row'} width="100%" justifyContent="space-between">
                <Button sx={{ minWidth: 130 }} disabled={isLoadingSave} variant="cancel" onClick={handleNavigateButton}>
                    Cancel
                </Button>
                <LoadingButton
                    sx={{
                        minWidth: 130,
                    }}
                    loading={isLoadingSave}
                    loadingPosition="center"
                    variant="contained"
                    disabled={
                        !(isValid && isDirty) ||
                        isLoadingSave ||
                        !imageElement ||
                        !methods.watch().geoZonePoints?.length
                    }
                    onClick={handleEditGeoZone}
                >
                    Save
                </LoadingButton>
            </Stack>
        </Stack>
    );
}

export const renderDetectModesOption = (option: DetectMode, detectModes: DetectMode[]) => {
    return (
        <Stack direction={'row'} width={"100%"} justifyContent={'space-between'} alignItems={'center'}>
            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                <Typography
                    variant="body1"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {option.Label}
                </Typography>
                {detectModes.length > 0 && option.Label === detectModes[0].Label && (
                    <Typography
                        variant="body1"
                        color={color.success}
                        sx={{
                            padding: '4px 8px',
                            fontSize: 12,
                            background: '#E5F5ED',
                            borderRadius: '5px',
                        }}
                    >
                        Recommend
                    </Typography>

                )}
            </Stack>
            <Tooltip title={option.Note} arrow placement="top">
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        p: 0.1,
                        background: (theme) => theme.palette.info.main,
                        borderRadius: '100%',
                    }}
                >
                    <BsQuestion color="#fff" fontSize={16} />
                </Stack>
            </Tooltip>
        </Stack>
    );
};